import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import Cookies from "js-cookie";
import axios from "axios";
import { useLocation } from "react-router-dom";
import ModalLoadingOverlay, {
  modalLoadingContainerSx,
} from "./ModalLoadingOverlay";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: 600 },
  height: "80vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 3,
  outline: "none",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const editorConfig = {
  toolbar: {
    items: [
      "heading",
      "|",
      "bold",
      "italic",
      "bulletedList",
      "numberedList",
      "blockQuote",
      "insertTable",
      "undo",
      "redo",
    ],
  },
  placeholder: "Write the Description here...",
};

const sectionLabel = (text) => (
  <Typography sx={{ fontWeight: 500, fontSize: "16px", color: "#000", mb: 1 }}>
    {text}
  </Typography>
);

const fieldLabel = (text, required = false) => (
  <Typography
    sx={{ fontSize: "14px", fontWeight: 400, color: "#000", mb: 0.5 }}
  >
    {text}
    {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
  </Typography>
);

const cancelBtnSx = {
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  fontWeight: 500,
  fontSize: "12px",
  borderRadius: "16px",
  minWidth: "132px",
  minHeight: "32px",
  px: 1.5,
  py: 1,
  backgroundColor: "#F8F9FB",
  color: "#6B7280",
  border: "1px solid #D0D5DD",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#F2F4F7",
    border: "1px solid #C3C9D4",
    boxShadow: "none",
  },
};

const primaryBtnSx = {
  backgroundColor: "#3F95EA",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 500,
  minWidth: "90px",
  minHeight: "32px",
  px: 1.5,
  py: 1,
  fontSize: "12px",
  "&:hover": { backgroundColor: "#2563EB" },
};

const StepTab = ({ label, index, activeStep, onTabClick, isClickable }) => {
  const isActive = index === activeStep;
  return (
    <Box
      onClick={() => isClickable && onTabClick(index)}
      sx={{
        display: "flex",
        alignItems: "center",
        pt: "10px",
        pb: "10px",
        borderBottom: isActive ? "2px solid #3F95EA" : "2px solid transparent",
        cursor: isClickable ? "pointer" : "default",
        px: 0.5,
        transition: "border-color 0.15s",
        "&:hover":
          isClickable && !isActive ? { borderBottom: "2px solid #90caf9" } : {},
      }}
    >
      <Typography
        fontSize="16px"
        fontWeight={isActive ? 600 : 400}
        color={isActive ? "#3F95EA" : isClickable ? "#6B7280" : "#9CA3AF"}
        noWrap
        sx={{
          transition: "color 0.15s",
          "&:hover": isClickable && !isActive ? { color: "#3F95EA" } : {},
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const AddBranch = ({
  open = true,
  onClose,
  onSuccess,
  disableNavigation = false,
  editBranchId,
  editMode,
  companyId,
  companyName,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const effectiveCompanyId = companyId ?? id;
  const branch_id = editBranchId ?? searchParams.get("b_id");
  const isEdit = editMode ?? searchParams.get("edit") === "1";
  const { company_name } = location.state || {};

  const [formState, setFormState] = useState({
    name: "",
    phone: "",
    company_id: "",
    company_name: "",
    addressLine1: "",
    addressLine2: "",
    state: "",
    city: "",
    zip: "",
    notes: "",
  });
  const displayCompanyName =
    formState.company_name || companyName || company_name || "";

  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Basic Details", "Additional Info"]; // ← 2 tabs only

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [selectedZipCode, setSelectedZipCode] = useState(0);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [cities, setCities] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [states, setStates] = useState([]);
  const [zipCodes, setZipCodes] = useState([]);

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleStateChange = (event) => {
    const val = event.target.value;
    setSelectedState(val);
    setFormState((prev) => ({ ...prev, state: val, city: "", zip: "" }));
    setSelectedCity("");
    setSelectedZipCode("");
    setZipCodes([]);
  };

  const handleCityChange = (event) => {
    const val = event.target.value;
    setSelectedCity(val);
    setSelectedZipCode("");
    setFormState((prev) => ({ ...prev, city: val }));
    fetchZipCodes(val);
  };

  const handleZipCodeChange = (event) => {
    const val = event.target.value;
    setSelectedZipCode(val);
    setFormState((prev) => ({ ...prev, zip: String(val) }));
  };

  const handleResponse = (response) => {
    if (response) {
      setSnackbarMessage(response.data.message?.msg);
      setSnackbarSeverity(response.data.success ? "success" : "error");
      setTimeout(() => setSnackbarOpen(true), 1500);
      setTimeout(() => {
        setLoading(false);
        if (disableNavigation) {
          if (onSuccess) onSuccess();
          if (onClose) onClose();
        } else {
          navigate(-1);
        }
      }, 2000);
    } else setLoading(false);
  };

  const handleClose = () => {
    if (disableNavigation) {
      if (onClose) onClose();
      return;
    }
    navigate(-1);
  };

  const getBranchInfo = async () => {
    const res = await axios.get(`/branch-info/${branch_id}`, {
      headers: {
        authorization:
          Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
      },
    });
    const d = res.data.message[0];
    setFormState((prev) => ({
      ...prev,
      ...d,
      company_name: d.company_name || companyName || company_name || "",
    }));
    setSelectedState(d.state);
    setSelectedCity(d.city);
    setSelectedZipCode(Number(d.zip));
  };

  const editCompanyBranch = async () => {
    try {
      const s = { ...formState, company_id: effectiveCompanyId };
      ["company_name", "date_created", "is_internal"].forEach(
        (k) => delete s[k],
      );
      return await axios.post(`/company/branch/edit/${branch_id}`, s, {
        headers: {
          "Content-Type": "application/json",
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
    } catch (err) {
      setSnackbarMessage(err?.response?.data?.error);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const createBranch = async () => {
    try {
      const s = { ...formState, company_id: effectiveCompanyId };
      ["company_name", "date_created"].forEach((k) => delete s[k]);
      return await axios.post("/company/branch/create", s, {
        headers: {
          "Content-Type": "application/json",
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
    } catch (err) {
      setSnackbarMessage(err?.response?.data?.error);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const fetchStates = async () => {
    try {
      const r = await axios.post(
        process.env.REACT_APP_JOBEZEURL + "/state-city/list",
        { search: "" },
      );
      const list = r?.data?.message || [];
      setStates(list.filter((item) => item?.valueType === "state"));
      setAllCities(list.filter((item) => item?.valueType === "city"));
    } catch (e) {
      console.error(e);
    }
  };
  const fetchCities = async (stateName) => {
    const selectedStateOption = states.find((state) => state.value === stateName);
    const filteredCities = selectedStateOption?.stateCode
      ? allCities.filter((city) => city.stateCode === selectedStateOption.stateCode)
      : [];
    setCities(filteredCities);
  };
  const fetchZipCodes = async (city) => {
    const selectedCityOption = allCities.find((item) => item.value === city);
    setZipCodes(selectedCityOption?.id ? [{ zip: selectedCityOption.id }] : []);
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await fetchStates();
        if (isEdit) {
          await getBranchInfo();
        } else {
          setFormState((prev) => ({
            ...prev,
            company_id: effectiveCompanyId || "",
            company_name: companyName || company_name || "",
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    fetchCities(selectedState);
  }, [allCities, selectedState, states]);

  useEffect(() => {
    fetchZipCodes(selectedCity);
  }, [allCities, selectedCity]);

  // Step 0 validates everything (basic + address)
  const validateStep = (step) => {
    const e = {};
    if (step === 0) {
      if (!formState.name) e.name = "Branch Name is required";
      if (!formState.phone) e.phone = "Contact Number is required";
      else if (formState.phone.length !== 10)
        e.phone = "It should contain exactly 10 digits.";
      if (!formState.addressLine1)
        e.addressLine1 = "Address Line 1 is required";
      if (!formState.state) e.state = "State is required";
      if (!formState.city) e.city = "City is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const isStepDataFilled = (step) => {
    if (step === 0)
      return (
        !!formState.name &&
        !!formState.phone &&
        formState.phone.length === 10 &&
        !!formState.addressLine1 &&
        !!formState.state &&
        !!formState.city
      );
    return true;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) setActiveStep((p) => p + 1);
  };
  const handleBack = () => {
    setErrors({});
    setActiveStep((p) => p - 1);
  };

  const handleTabClick = (target) => {
    if (target === activeStep) return;
    if (target < activeStep) {
      setErrors({});
      setActiveStep(target);
      return;
    }
    let ok = true;
    for (let i = activeStep; i < target; i++) {
      if (!isStepDataFilled(i)) {
        validateStep(i);
        ok = false;
        break;
      }
    }
    if (ok) {
      setErrors({});
      setActiveStep(target);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(0)) {
      setActiveStep(0);
      return;
    }
    setLoading(true);
    if (isEdit) handleResponse(await editCompanyBranch());
    else handleResponse(await createBranch());
  };

  return (
    <Box>
      <Modal open={open}>
        <Box sx={modalLoadingContainerSx(loading, modalStyle)}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "20px",
              py: "8px",
              borderBottom: "1px solid #E5E7EB",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{ fontSize: "18px", fontWeight: 600, color: "#111827" }}
            >
              {isEdit ? "Edit Branch Details" : "Add Branch"}
            </Typography>
            <Button
              onClick={handleClose}
              sx={{ minWidth: 0, p: 0.5, color: "#6B7280" }}
            >
              <CloseIcon sx={{ fontSize: "18px" }} />
            </Button>
          </Box>

          {/* Tabs */}
          <Box
            sx={{
              borderBottom: "1px solid #E5E7EB",
              backgroundColor: "#F9FAFB",
              flexShrink: 0,
              px: "20px",
            }}
          >
            <Box display="flex" gap={3}>
              {steps.map((label, idx) => {
                const canClick =
                  idx !== activeStep &&
                  (() => {
                    for (let i = 0; i < idx; i++) {
                      if (!isStepDataFilled(i)) return false;
                    }
                    return true;
                  })();
                return (
                  <StepTab
                    key={label}
                    label={label}
                    index={idx}
                    activeStep={activeStep}
                    onTabClick={handleTabClick}
                    isClickable={canClick}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Scrollable Body */}
          <Box sx={{ overflowY: "auto", flex: 1 }}>
            {/* ══ TAB 0 — Basic Details + Address + State/City (all in one) ══ */}
            {activeStep === 0 && (
              <Box display="flex" flexDirection="column" gap={1.5} p={2.5}>
                {sectionLabel("Basic Details")}

                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    {fieldLabel("Branch Name", true)}
                    <TextField
                      name="name"
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="Enter branch name"
                      inputProps={{ maxLength: 60 }}
                      value={formState.name}
                      onChange={handleInputChange}
                      onFocus={() => setErrors((p) => ({ ...p, name: "" }))}
                      error={!!errors.name}
                      helperText={errors.name}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    {fieldLabel("Company Name")}
                    <TextField
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={displayCompanyName}
                      disabled
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  {fieldLabel("Contact Number", true)}
                  <TextField
                    name="phone"
                    variant="outlined"
                    fullWidth
                    type="tel"
                    size="small"
                    placeholder="Enter 10-digit number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">+1</InputAdornment>
                      ),
                      inputProps: {
                        maxLength: 10,
                        onKeyPress: (e) => {
                          if (e.which > 31 && (e.which < 48 || e.which > 57))
                            e.preventDefault();
                        },
                        pattern: "[0-9]*",
                        onPaste: (e) => e.preventDefault(),
                      },
                    }}
                    value={formState.phone}
                    onChange={handleInputChange}
                    onFocus={() => setErrors((p) => ({ ...p, phone: "" }))}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Box>

                <Divider />
                {sectionLabel("Address")}

                <Box>
                  {fieldLabel("Address Line 1", true)}
                  <TextField
                    name="addressLine1"
                    variant="outlined"
                    fullWidth
                    size="small"
                    placeholder="Enter address line 1"
                    value={formState.addressLine1}
                    onChange={handleInputChange}
                    onFocus={() =>
                      setErrors((p) => ({ ...p, addressLine1: "" }))
                    }
                    error={!!errors.addressLine1}
                    helperText={errors.addressLine1}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Box>

                <Box>
                  {fieldLabel("Address Line 2")}
                  <TextField
                    name="addressLine2"
                    variant="outlined"
                    fullWidth
                    size="small"
                    placeholder="Enter address line 2 (optional)"
                    value={formState.addressLine2}
                    onChange={handleInputChange}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Box>

                <Divider />
                {sectionLabel("State & City")}

                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    {fieldLabel("State", true)}
                    <FormControl fullWidth size="small" error={!!errors.state}>
                      <Select
                        value={selectedState}
                        displayEmpty
                        onChange={handleStateChange}
                        onFocus={() => setErrors((p) => ({ ...p, state: "" }))}
                        renderValue={(v) =>
                          v || (
                            <span style={{ color: "rgba(106,120,133,0.60)" }}>
                              Select
                            </span>
                          )
                        }
                        sx={{ borderRadius: "8px" }}
                      >
                        {states?.map((s) => (
                          <MenuItem key={s.id} value={s.value}>
                            {s.value}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.state && (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#ef4444",
                            px: 1.5,
                            pt: 0.5,
                          }}
                        >
                          {errors.state}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    {fieldLabel("City", true)}
                    <FormControl fullWidth size="small" error={!!errors.city}>
                      <Select
                        value={selectedCity}
                        displayEmpty
                        onChange={handleCityChange}
                        onFocus={() => setErrors((p) => ({ ...p, city: "" }))}
                        disabled={!selectedState}
                        renderValue={(v) =>
                          v || (
                            <span style={{ color: "rgba(106,120,133,0.60)" }}>
                              Select
                            </span>
                          )
                        }
                        sx={{ borderRadius: "8px" }}
                      >
                        {cities?.map((c) => (
                          <MenuItem key={c.id} value={c.value}>
                            {c.value}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.city && (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#ef4444",
                            px: 1.5,
                            pt: 0.5,
                          }}
                        >
                          {errors.city}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                </Box>

                <Box sx={{ width: "50%" }}>
                  {fieldLabel("Zip Code")}
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedZipCode || ""}
                      displayEmpty
                      onChange={handleZipCodeChange}
                      disabled={!selectedCity}
                      renderValue={(v) =>
                        v || (
                          <span style={{ color: "rgba(106,120,133,0.60)" }}>
                            Select
                          </span>
                        )
                      }
                      sx={{ borderRadius: "8px" }}
                    >
                      {zipCodes?.map((z) => (
                        <MenuItem key={z.zip} value={z.zip}>
                          {z.zip}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

            {/* ══ TAB 1 — Additional Info ══ */}
            {activeStep === 1 && (
              <Box display="flex" flexDirection="column" gap={1.5} p={2.5}>
                {sectionLabel("Additional Info")}
                {fieldLabel("Notes / Description")}
                <Box
                  sx={{
                    "& .ck-editor__editable": {
                      minHeight: "200px !important",
                      maxHeight: "320px !important",
                      overflowY: "auto !important",
                    },
                  }}
                >
                  <CKEditor
                    editor={ClassicEditor}
                    config={editorConfig}
                    data={formState.notes}
                    onChange={(_, editor) =>
                      setFormState((prev) => ({
                        ...prev,
                        notes: editor.getData(),
                      }))
                    }
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box sx={{ flexShrink: 0, borderTop: "1px solid rgba(0,0,0,0.12)" }}>
            <Box
              sx={{
                px: 3,
                py: 1.5,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
              }}
            >
              <Button
                variant="contained"
                onClick={activeStep === 0 ? handleClose : handleBack}
                sx={cancelBtnSx}
                disableElevation
              >
                {activeStep === 0 ? "Cancel" : "Back"}
              </Button>
              {activeStep < 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={primaryBtnSx}
                  disableElevation
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={primaryBtnSx}
                  disableElevation
                >
                  {isEdit ? "Save" : "Add"}
                </Button>
              )}
            </Box>
          </Box>
          <ModalLoadingOverlay open={loading} />
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddBranch;
